import React from 'react';
import {Dialog, DialogProps} from 'primereact/dialog';
import {createGlobalStyle} from "styled-components";
import {FileUpload, FileUploadProps} from "primereact/fileupload";
import {clsx} from "clsx";
import {CustomErrorMessage} from "@/Messages/Toast/General/CustomErrorMessage";
import {useQueryClient} from "@tanstack/react-query";
import {useToastMessagesStore} from "@/Stores/ToastMessagesStore";
import {confirmPopup, ConfirmPopup} from "primereact/confirmpopup";
import {useUploadGtaRateConfig} from "@/Service/Api/V1/ApiHooks/GtaRates/useUploadGtaRateConfig";
import {QueryKeys} from "@/Service/Api/V1/QueryKeys/QueryKeys";
import ErrorsBlock from "@/Components/Forms/Form/ErrorsBlock";

export type UploadGtaRatesConfigModalProps = {
    dialogProps: DialogProps,
    fileUploadProps?: FileUploadProps,
};

const UploadGtaRatesConfigModalStyle = createGlobalStyle`
    .upload-gta-rates-config-modal {
        width: 50%;
    }
`;

const UploadGtaRatesConfigModal: React.FC<UploadGtaRatesConfigModalProps> = ({
                                                                                 dialogProps,
                                                                                 fileUploadProps
                                                                             }: UploadGtaRatesConfigModalProps) => {
    const queryClient = useQueryClient();
    const addToastMessage = useToastMessagesStore((state) => state.addToastMessage);
    const fileUploadRef = React.useRef<FileUpload>(null);
    const {mutateAsync: uploadFile, error: uploadFileError} = useUploadGtaRateConfig();

    const uploadHandler = async (event: MouseEvent) => {
        confirmPopup({
            target: event.target as HTMLElement,
            message: 'Please confirm to proceed moving forward.',
            icon: 'pi pi-exclamation-triangle',
            defaultFocus: 'accept',
            accept: async () => {
                await uploadFile({
                    file: fileUploadRef.current?.getFiles()[0]
                }, {
                    onSuccess: () => {
                        addToastMessage({
                            severity: 'success',
                            summary: 'GTA Rate config uploaded',
                            detail: 'GTA Rate config has been uploaded successfully',
                        });
                        queryClient.invalidateQueries({
                            queryKey: QueryKeys.gtaRates._def
                        });
                        queryClient.invalidateQueries({
                            queryKey: QueryKeys.gtaRates._def
                        });
                        dialogProps.onHide();
                    },
                    onError: error => {
                        addToastMessage(CustomErrorMessage(error));
                    }
                });
            },
            reject: () => {

            }
        });


    };

    return (
        <>
            <UploadGtaRatesConfigModalStyle/>
            <Dialog
                {...dialogProps}
                className={clsx(dialogProps?.className, 'upload-gta-rates-config-modal')}
                header={'Upload Root Causes Config'}
                draggable={false}
                blockScroll
            >
                <div className={'w-full'}>
                    <FileUpload
                        ref={fileUploadRef}
                        mode={fileUploadProps?.mode ?? 'advanced'}
                        multiple={fileUploadProps?.multiple ?? false}
                        cancelOptions={{style: {display: 'none'}}}
                        accept={fileUploadProps?.accept ?? '.csv'}
                        emptyTemplate={fileUploadProps?.emptyTemplate ?? 'Drag and drop your CSV file here'}
                        customUpload={fileUploadProps?.customUpload ?? true}
                        pt={{
                            uploadButton: {root: {onClick: uploadHandler}}
                        }}
                    />
                    <ConfirmPopup/>
                    <ErrorsBlock
                        className="block mt-2 p-error"
                        errorMessages={uploadFileError?.message}
                    />
                </div>
            </Dialog>
        </>
    );
};

export default UploadGtaRatesConfigModal;
